import {
    ScheduleDiff,
    TradingHoursBlacklistVersionsResult,
    TradingHoursHistoryView,
    TradingHoursHolidayHistoryView,
    TradingHoursRecordBlacklistResult,
    TradingHoursRecordOverridesResult,
    TradingHoursRecordOverrideView,
    TradingHoursScheduleStatusView,
    TradingHoursUnfreezeResult,
    TradingHoursVersionsView,
} from '@thinkalpha/quant-ws-client/contracts/trading-hours-admin.js';
import type {
    TradingHoursBlacklistVersionsRequest,
    TradingHoursFreezeRequest,
    TradingHoursHistoryQuery,
    TradingHoursRecordBlacklistRequest,
} from '@thinkalpha/quant-ws-client/contracts/trading-hours-admin.js';
import { createInstance } from 'src/api';
import { appConfig } from 'src/lib/config';

/**
 * HTTP wrappers around the quant-ws TradingHours worker's superuser levers
 * (`/admin/trading-hours`). Responses are parsed with the shared zod contracts so
 * `Instant` / `LocalDate` codecs decode on the way in; request bodies are plain JSON.
 *
 * Backend swagger (worker host, superuser only):
 *   GET    /admin/trading-hours/versions?limit=         — recent ingestions, effective + pin state
 *   GET    /admin/trading-hours/schedule-status          — effective vs latest, diff, pin, serving
 *   GET    /admin/trading-hours/history?ingestionId=     — lever events + effective-version timeline
 *   POST   /admin/trading-hours/freeze                   — pin (and by default blacklist current). `POST /pin` is the same
 *                                                        lever without the blacklist option, so the UI does not use it.
 *   POST   /admin/trading-hours/unfreeze                 — preview; `{ confirm: true }` applies
 *   POST   /admin/trading-hours/versions/blacklist       — discard / restore whole vendor updates
 *   POST   /admin/trading-hours/records/blacklist        — switch single records off / on
 *   GET    /admin/trading-hours/records/overrides        — active durable record overrides
 *   DELETE /admin/trading-hours/records/overrides/:id    — release one override
 *   GET    /admin/trading-hours/diff?from=&to=           — vendor-content diff between versions
 *   GET    /admin/trading-hours/holidays/history         — one market's holiday on one date, per version
 *   GET    /admin/trading-hours/backup?ingestionId=      — the raw vendor zip
 */

const axios = createInstance({ baseURL: appConfig.tradingHoursAdminApi }, { useLegacySerialization: false });

const BASE = '/admin/trading-hours';

export const getScheduleStatus = async (): Promise<TradingHoursScheduleStatusView> => {
    const resp = await axios.get<unknown>(`${BASE}/schedule-status`);
    return TradingHoursScheduleStatusView.parse(resp.data);
};

export const getVersions = async (limit: number): Promise<TradingHoursVersionsView> => {
    const resp = await axios.get<unknown>(`${BASE}/versions`, { params: { limit } });
    return TradingHoursVersionsView.parse(resp.data);
};

export const getHistory = async (query: Partial<TradingHoursHistoryQuery>): Promise<TradingHoursHistoryView> => {
    const resp = await axios.get<unknown>(`${BASE}/history`, { params: query });
    return TradingHoursHistoryView.parse(resp.data);
};

export const getRecordOverrides = async (): Promise<TradingHoursRecordOverridesResult> => {
    const resp = await axios.get<unknown>(`${BASE}/records/overrides`);
    return TradingHoursRecordOverridesResult.parse(resp.data);
};

export const getDiff = async (from: string, to: string): Promise<ScheduleDiff> => {
    const resp = await axios.get<unknown>(`${BASE}/diff`, { params: { from, to } });
    return ScheduleDiff.parse(resp.data);
};

export interface HolidayHistoryParams {
    /** ISO date, `YYYY-MM-DD`. */
    date: string;
    mic?: string;
    finId?: string;
}

export const getHolidayHistory = async (params: HolidayHistoryParams): Promise<TradingHoursHolidayHistoryView> => {
    const resp = await axios.get<unknown>(`${BASE}/holidays/history`, { params });
    return TradingHoursHolidayHistoryView.parse(resp.data);
};

export const freeze = async (request: TradingHoursFreezeRequest): Promise<TradingHoursScheduleStatusView> => {
    const resp = await axios.post<unknown>(`${BASE}/freeze`, request);
    return TradingHoursScheduleStatusView.parse(resp.data);
};

export const unfreeze = async (confirm: boolean): Promise<TradingHoursUnfreezeResult> => {
    const resp = await axios.post<unknown>(`${BASE}/unfreeze`, { confirm });
    return TradingHoursUnfreezeResult.parse(resp.data);
};

export const setVersionsBlacklist = async (
    request: TradingHoursBlacklistVersionsRequest,
): Promise<TradingHoursBlacklistVersionsResult> => {
    const resp = await axios.post<unknown>(`${BASE}/versions/blacklist`, request);
    return TradingHoursBlacklistVersionsResult.parse(resp.data);
};

export const setRecordsBlacklist = async (
    request: TradingHoursRecordBlacklistRequest,
): Promise<TradingHoursRecordBlacklistResult> => {
    const resp = await axios.post<unknown>(`${BASE}/records/blacklist`, request);
    return TradingHoursRecordBlacklistResult.parse(resp.data);
};

export const releaseRecordOverride = async (
    id: string,
    reason: string | undefined,
): Promise<TradingHoursRecordOverrideView> => {
    const resp = await axios.delete<unknown>(`${BASE}/records/overrides/${encodeURIComponent(id)}`, {
        params: reason ? { reason } : undefined,
    });
    return TradingHoursRecordOverrideView.parse(resp.data);
};

/** The raw vendor zip of one ingestion. The instance's JSON parse is bypassed for the binary body. */
export const downloadArchive = async (ingestionId: string): Promise<Blob> => {
    const resp = await axios.get<Blob>(`${BASE}/backup`, {
        params: { ingestionId },
        responseType: 'blob',
        transformResponse: [(data: Blob) => data],
    });
    return resp.data;
};
